import { Input } from '@/components/ui/input'
import { FormRelationType, FormFieldProps } from './type'
import {
  Command,
  CommandEmpty,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { CommandInput } from '@/components/ui/command'
import { CheckIcon, XIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Badge } from '@/components/ui/badge'
import { useOffsetInfiniteList } from '@/@diffrag/hooks/useOffsetInfiniteList'
import { cn } from '@/lib/utils'

type Item = {
  id: string
  title: string
}

export const FormRelationField = ({
  data,
  field,
}: FormFieldProps<FormRelationType>) => {
  const isReadOnly = data.readOnly ?? false
  const multiple = data.multiple ?? false

  const [open, setOpen] = useState(false)

  const [searchText, setSearchText] = useState('')

  const { rows, listBottom } = useOffsetInfiniteList({
    id: `relation-field-search-${field.name}`,
    fn: data.searchApiFn,
    params: {
      amount: 20,
      page: 1,
      searchText,
    },
    enabled: open && !!data.searchApiFn,
  })

  const onAdd = (value: Item) => {
    if (isSelected(value)) {
      onRemove(value)
      return
    }

    if (multiple) {
      field.onChange([...(field.value || []), value])
    } else {
      field.onChange(value)
      setOpen(false)
    }
  }

  const onRemove = (value: Item) => {
    if (multiple) {
      field.onChange([
        ...(field.value || []).filter((k: Item) => k.id !== value.id),
      ])
    } else {
      field.onChange(null)
    }
  }

  const isSelected = (item: Item) => {
    if (multiple) {
      return field.value?.includes(item.id)
    }

    return field.value?.id === item.id
  }

  const badges = useMemo(() => {
    if (!field.value) {
      return []
    }

    if (data.multiple) {
      return field.value ?? []
    }

    return [field.value]
  }, [field.value, data.multiple])

  const onRemoveBadge = (badge: Item) => {
    onRemove(badge)
  }

  return (
    <div className="space-y-2">
      {!!badges.length && (
        <div className="flex min-h-8 flex-wrap gap-1 rounded bg-white py-0.5">
          {badges.map((badge: Item) => (
            <Badge
              key={badge.id}
              className={cn('flex h-7 items-center gap-1', {
                'cursor-pointer': data.href,
              })}
              onClick={() => {
                if (data.href) {
                  window.open(data.href(badge), '_blank')
                }
              }}
            >
              <span>{data.labelKey ? badge[data.labelKey] : badge.title}</span>
              {!data.readOnly && (
                <XIcon
                  className="size-4 cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onRemoveBadge(badge)
                    data.onRemove?.(badge)
                  }}
                />
              )}
            </Badge>
          ))}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        {multiple || (!multiple && !badges.length) ? (
          <div className="flex items-center gap-2">
            <PopoverTrigger>
              <Input
                disabled={isReadOnly}
                className="w-48 disabled:opacity-100"
                placeholder="검색어를 입력해주세요."
              />
            </PopoverTrigger>
          </div>
        ) : undefined}
        <PopoverContent className="w-[500px] p-0">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="검색어를 입력해주세요."
              className="h-9 text-[13px]"
              onValueChange={setSearchText}
              value={searchText}
            />
            <CommandList>
              <CommandEmpty>결과가 없습니다.</CommandEmpty>
              {rows?.map((item) => (
                <CommandItem
                  key={item.id}
                  onSelect={() => {
                    onAdd(item)
                    data.onAdd?.(item)
                  }}
                  className="flex min-h-7 gap-4"
                >
                  <div className="flex flex-1 items-center gap-2">
                    <span className="text-[13px]">
                      {data.labelKey ? item[data.labelKey] : item.title}
                    </span>
                  </div>
                  {isSelected(item) && <CheckIcon className="size-4" />}
                </CommandItem>
              ))}
              <div ref={listBottom} />
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
